import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("dg_cart") || "[]");
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("dg_cart", JSON.stringify(items));
  }, [items]);

  const addItem = (product, qty = 1, variant = null) => {
    const pid = variant ? variant.id : product.id;
    const name = variant ? variant.name : product.name;
    const price = variant ? variant.price : product.price;
    const availability = product.category === "clones" ? (product.availability || "preorder") : "ready";
    setItems((prev) => {
      const existing = prev.find((i) => i.product_id === pid);
      if (existing) {
        return prev.map((i) =>
          i.product_id === pid ? { ...i, quantity: i.quantity + qty } : i
        );
      }
      return [
        ...prev,
        {
          product_id: pid,
          name,
          price,
          image_url: product.image_url,
          category: product.category,
          availability,
          quantity: qty,
        },
      ];
    });
    toast.success(`${name} added to cart`);
    setOpen(true);
  };

  const updateQty = (productId, qty) => {
    if (qty <= 0) return removeItem(productId);
    setItems((prev) =>
      prev.map((i) => (i.product_id === productId ? { ...i, quantity: qty } : i))
    );
  };

  const removeItem = (productId) =>
    setItems((prev) => prev.filter((i) => i.product_id !== productId));

  const clearCart = () => setItems([]);

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  // Promo code DARK25: 25% off entire order, valid only with more than one clone
  const cloneQty = items.filter((i) => i.category === "clones").reduce((s, i) => s + i.quantity, 0);
  const discountEligible = cloneQty > 1;
  const hasPreorder = items.some((i) => i.availability === "preorder");
  const hasReady = items.some((i) => i.availability && i.availability !== "preorder");

  return (
    <CartContext.Provider
      value={{ items, addItem, updateQty, removeItem, clearCart, subtotal, count, open, setOpen, cloneQty, discountEligible, hasPreorder, hasReady }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
